import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import {
  BaseInputStyleProps,
  baseInputStylesMap,
  defaultBaseInputStyles,
} from "@/components/global/form/inputs/styles/base-input.styles";
import {
  InputWrapperStyleProps,
  defaultInputWrapperStyles,
  inputWrapperStylesMap,
} from "@/components/global/form/inputs/styles/input-wrapper.styles";
import Icon from "@/components/icon";

interface TextAreaInputProps {
  error?: string;
  isValid?: boolean;
  required?: boolean;
  disabled?: boolean;
  dataCy?: string;
  placeholder?: string;
  wrapperStyles?: InputWrapperStyleProps;
  styles?: BaseInputStyleProps;
  [key: string]: any;
}

export const TextAreaInput = forwardRef<
  HTMLTextAreaElement,
  TextAreaInputProps
>(
  (
    {
      error,
      isValid,
      required: _required,
      disabled,
      dataCy,
      styles,
      wrapperStyles = {},
      ...props
    }: TextAreaInputProps,
    ref,
  ) => {
    const mergedStyles = { ...defaultBaseInputStyles, ...styles };
    const mergedWrapperStyles = {
      ...defaultInputWrapperStyles,
      ...wrapperStyles,
    };

    return (
      <div className="w-full relative">
        <textarea
          ref={ref}
          className={twMerge(
            "w-full bg-white autofill:bg-transparent rounded-md resize-y",
            "min-h-[150px] py-3",
            "overflow-y-auto",
            "border focus:outline-none focus:ring-0",
            inputWrapperStylesMap.rounded[mergedWrapperStyles.rounded!],
            `border-${mergedWrapperStyles.borderColor}`,
            mergedStyles.placeholderStyle === "italic" && "placeholder:italic",
            baseInputStylesMap.padding[mergedStyles.padding?.left || "md"],
            baseInputStylesMap.padding[mergedStyles.padding?.right || "lg"],
            error && [
              `border-${mergedWrapperStyles.errorColor}`,
              "bg-danger3",
              `text-${mergedStyles.textColor}`,
            ],
            disabled && [
              "cursor-not-allowed",
              `bg-${mergedWrapperStyles.disabledStyle?.backgroundColor}`,
              `border-${mergedWrapperStyles.disabledStyle?.borderColor}`,
              `text-${mergedWrapperStyles.disabledStyle?.textColor}`,
            ],
            mergedStyles.className,
          )}
          style={{
            height: "150px",
          }}
          disabled={disabled}
          data-cy={dataCy}
          {...props}
        />
        <div className="flex items-center gap-2 absolute right-2 top-3">
          {error && <Icon icon="cross" size={25} className="text-primary" />}
          {isValid && !error && (
            <Icon icon="check-simple" size={25} className="text-success2" />
          )}
        </div>
      </div>
    );
  },
);

TextAreaInput.displayName = "TextAreaInput";
